import { useEffect, useRef, useCallback } from "react";
import useServiceCallsStore from "../stores/serviceCallsStore";
import useDeliveriesStore from "../stores/deliveriesStore";
import { isGuarded } from "../lib/mutationGuard";

// iter 393f (scale audit r8) — jittered, coalesced full-list refetches.
// Broadcast events used to make EVERY connected client refetch the full
// list at the same instant (thundering herd at scale). Now: bursts of
// events collapse into one refetch, spread over a 0.3-4s random window.
const _pendingRefetch = {};
function jitteredRefetch(key, fn) {
  if (_pendingRefetch[key]) return;
  _pendingRefetch[key] = setTimeout(() => {
    delete _pendingRefetch[key];
    fn();
  }, 300 + Math.random() * 3700);
}
import BACKEND_URL from "../lib/apiConfig";

/**
 * Connects to the backend WebSocket and pushes real-time updates
 * into the Zustand stores. Handles reconnection with exponential backoff + jitter
 * so a brief network hiccup doesn't trigger a reconnect storm.
 *
 * Usage: call useWebSocket() once in a top-level component (e.g. Layout).
 */
export function useWebSocket() {
  const wsRef = useRef(null);
  const reconnectTimer = useRef(null);
  const pingTimer = useRef(null);
  const mountedRef = useRef(true);
  const reconnectAttempts = useRef(0);
  const lastConnectedAt = useRef(0);
  const everConnectedRef = useRef(false);

  const connect = useCallback(() => {
    if (!mountedRef.current) return;
    if ([WebSocket.OPEN, WebSocket.CONNECTING].includes(wsRef.current?.readyState)) return;

    const httpUrl = BACKEND_URL;
    const wsUrl = httpUrl.replace(/^https/, "wss").replace(/^http/, "ws") + "/api/ws";

    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        if (!mountedRef.current || wsRef.current !== ws) {
          ws.close();
          return;
        }
        window.__wsConnected = true;
        window.dispatchEvent(new CustomEvent("ws_status", { detail: { connected: true } }));
        // iter 372u — catch-up after reconnect: any events missed while
        // disconnected (laptop sleep, network blip) are gone, so tell
        // data hooks to revalidate. Only fires on RE-connects.
        if (everConnectedRef.current) {
          window.dispatchEvent(new CustomEvent("ws_reconnected"));
        }
        everConnectedRef.current = true;
        // Reset backoff after a successful connection that lasts >5s
        lastConnectedAt.current = Date.now();
        setTimeout(() => {
          if (ws.readyState === WebSocket.OPEN) reconnectAttempts.current = 0;
        }, 5000);
        if (pingTimer.current) clearInterval(pingTimer.current);
        pingTimer.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) ws.send("ping");
        }, 25000);
      };

      ws.onmessage = (evt) => {
        try {
          const msg = JSON.parse(evt.data);
          handleEvent(msg);
        } catch {
          // ignore non-JSON (e.g. pong)
        }
      };

      ws.onclose = () => {
        if (!mountedRef.current || wsRef.current !== ws) return;
        window.__wsConnected = false;
        window.dispatchEvent(new CustomEvent("ws_status", { detail: { connected: false } }));
        scheduleReconnect();
      };

      ws.onerror = () => {
        // onclose will fire after this
      };
    } catch {
      scheduleReconnect();
    }
  }, []);

  const scheduleReconnect = useCallback(() => {
    if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
    // Exponential backoff with jitter: 1s, 2s, 4s, 8s, 16s, capped at 30s
    const attempt = reconnectAttempts.current++;
    const exp = Math.min(1000 * Math.pow(2, attempt), 30000);
    const jitter = Math.random() * (exp * 0.3);
    const delay = exp + jitter;
    reconnectTimer.current = setTimeout(() => {
      if (mountedRef.current) connect();
    }, delay);
  }, [connect]);

  // Dispatch incoming events to the right store + broadcast globally
  const handleEvent = useCallback((msg) => {
    const { event, data } = msg;
    if (!event || event === "pong") return;
    if (event === "ping") {
      // Mobile timers may be throttled; answer the server's heartbeat too.
      const socket = wsRef.current;
      if (socket?.readyState === WebSocket.OPEN) {
        try { socket.send("ping"); } catch { socket.close(); }
      }
      return;
    }

    // Real-time delta merge for service_calls — patch the changed call in
    // the Zustand store directly instead of re-fetching everything.
    if (event === "service_call.updated" && data?.call?.id) {
      try {
        const incoming = { ...data.call };
        // Airtable lookup phones arrive as arrays on raw docs — the board's
        // filters call .trim() and crash on a non-string.
        if (Array.isArray(incoming.customer_phone)) incoming.customer_phone = incoming.customer_phone[0] || "";
        useServiceCallsStore.setState((state) => ({
          calls: state.calls.map((c) =>
            (c.id === incoming.id || c.airtable_id === incoming.id) &&
            // iter 398 — a delta for a row we're mid-mutation on may be stale
            // (e.g. from a concurrent Airtable sync); our local copy wins.
            !(c._syncing || isGuarded(c.id) || isGuarded(c.airtable_id))
              ? { ...c, ...incoming }
              : c
          ),
        }));
      } catch {
        // Fallback to a full refresh if the store shape changed
        useServiceCallsStore.getState().refreshCalls();
      }
    } else if (event.startsWith("service_call.") || event === "sync.complete") {
      jitteredRefetch("service_calls", () => useServiceCallsStore.getState().refreshCalls());
    }

    // Real-time delta merge for deliveries.
    if (event === "delivery.updated" && data?.delivery?.id) {
      try {
        useDeliveriesStore.setState((state) => ({
          deliveries: (state.deliveries || []).map((d) =>
            (d.id === data.delivery.id || d.airtable_id === data.delivery.id) &&
            !(d._syncing || isGuarded(d.id) || isGuarded(d.airtable_id))
              ? { ...d, ...data.delivery }
              : d
          ),
        }));
        window.dispatchEvent(new CustomEvent("delivery_update", { detail: msg }));
      } catch {
        useDeliveriesStore.getState().fetchDeliveries();
      }
    } else if (event.startsWith("delivery.") || event === "sync.complete") {
      jitteredRefetch("deliveries", () => useDeliveriesStore.getState().fetchDeliveries());
      window.dispatchEvent(new CustomEvent("delivery_update", { detail: msg }));
    }

    if (event.startsWith("street_legal.")) {
      // Pass full payload through so listeners can patch state in-place
      // instead of triggering an expensive full refresh.
      window.dispatchEvent(new CustomEvent("street_legal_update", { detail: msg }));
    }

    // Broadcast ALL events globally so any page can listen
    window.dispatchEvent(new CustomEvent("ws_event", { detail: { ...msg, data } }));
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    // Let StrictMode's immediate mount/cleanup finish before starting a handshake.
    const initialConnectTimer = setTimeout(connect, 0);

    // Reconnect when tab becomes visible (sleep recovery)
    const onVisibility = () => {
      if (document.hidden) return;
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        try { wsRef.current.send("ping"); } catch { wsRef.current.close(); }
      } else {
        reconnectAttempts.current = 0; // user came back, try immediately
        connect();
      }
    };
    document.addEventListener("visibilitychange", onVisibility);

    // Reconnect on browser network online event
    const onOnline = () => {
      reconnectAttempts.current = 0;
      connect();
    };
    window.addEventListener("online", onOnline);

    return () => {
      mountedRef.current = false;
      clearTimeout(initialConnectTimer);
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      if (pingTimer.current) clearInterval(pingTimer.current);
      if (wsRef.current) {
        const ws = wsRef.current;
        ws.onclose = null; // prevent reconnect on intentional close
        ws.onmessage = null;
        // Closing CONNECTING sockets makes browsers report a spurious failed
        // connection. Finish the handshake, then close without app callbacks.
        if (ws.readyState === WebSocket.CONNECTING) ws.onopen = () => ws.close();
        else ws.close();
        wsRef.current = null;
      }
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("online", onOnline);
    };
  }, [connect]);
}
